import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Phone, Video } from 'lucide-react'
import TooltipOverlay from './TooltipOverlay'
import { Button } from './ui/button'
import { useAppStore } from '@/store'
import { MODAL_TYPE } from '@/store/modalSlice'
import { IN_APP_PANEL_SIZE } from '@/store/inAppMeetingSlice'
import { useEffect, useState } from 'react'
import { useTranslation } from '@/i18n'
import { getCurrentDirection } from '@/i18n/config'
import clsx from 'clsx'
import { toast } from 'sonner'
import { trackEvent } from '@/utils/analytics'
import type { Agent } from '@/components/AgentSelector'
import { isFabricEmbedMode } from '@/lib/fabricEmbedAuth'
import { isFeatureAllowedInEmbed } from '@/lib/embedFeatures'
import {
  closeCallWindow,
  focusCallWindow,
  launchCall,
  rememberFrameOrigin,
  reserveCallWindow,
} from '@/lib/meetingCallWindow'

interface LLMCallDropdownProps {
  /** Header-sized ghost triggers (18px icons) for panels whose header uses plain icon buttons. */
  compact?: boolean
}

const LLMCallDropdown = ({ compact = false }: LLMCallDropdownProps = {}) => {
  const { t } = useTranslation()
  const {
    openModal,
    agents,
    fetchAgents,
    setSelectedAgentForCall,
    context_id,
    boardList,
    createBoard,
    generateDefaultBoardName,
    setContextId,
    startInAppMeeting,
    activeToken,
    panelPosition,
  } = useAppStore()
  const isRTL = getCurrentDirection() === 'rtl'
  // Retrieval scope for the call this dropdown starts. Local (not the chat store's
  // `entireDataCatalog`) on purpose: the two are separate decisions — a call's scope is fixed
  // for its lifetime on the meeting token, while chat resolves its own per turn.
  const [entireDataCatalog, setEntireDataCatalog] = useState(false)

  // Fetch agents when component mounts
  useEffect(() => {
    if (fetchAgents) {
      fetchAgents()
    }
  }, [fetchAgents])

  // Helper to get or create a workspace context. FS-553: a call needs a
  // workspace, so when the user has no board we create their first one on
  // demand (a normal, named board — no more auto-created Master Board).
  const getWorkspaceContext = async (): Promise<string | null> => {
    if (context_id) return context_id

    if (boardList.length > 0) {
      const sortedBoards = [...boardList].sort((a, b) => {
        const timeB = b.created_at ? new Date(b.created_at).getTime() : 0
        const timeA = a.created_at ? new Date(a.created_at).getTime() : 0
        return timeB - timeA
      })
      const workspaceId = sortedBoards[0].id
      setContextId(workspaceId)
      return workspaceId
    } else {
      const newBoard = await createBoard(generateDefaultBoardName())
      if (newBoard) {
        setContextId(newBoard.id)
        return newBoard.id
      }
    }
    return null
  }

  // Filter only online agents
  const onlineAgents = (agents || []).filter(
    (agent) => agent.availableForMeeting
  )

  const handleStartInApp = async (agent: Agent) => {
    // Embedded in Fabric the call cannot run in this frame — the portal's iframe
    // does not delegate `microphone` and Permissions Policy is inherited, so an
    // in-frame session connects and never hears the user. Run it in a popup.
    const embedded = isFabricEmbedMode()

    // Refuse to start a second call over a live one. Without this guard the new
    // token replaces `activeToken` and the first session is never stopped
    // server-side, and in embed mode the failure path below would close the
    // window belonging to the call still in progress.
    if (activeToken) {
      if (embedded) {
        focusCallWindow()
      }
      toast.error(
        t('chat:call.errors.already_in_call', {
          defaultValue: 'You are already in a call. End it before starting another.',
        })
      )
      return
    }

    // Reserve the window synchronously, on the click itself. Everything below
    // awaits (workspace lookup, then start-direct) and transient user activation
    // does not survive that, so opening later is intermittently popup-blocked.
    // Dock it beside the call panel, so the window reads as the panel's chat rather than a second call.
    if (embedded && !reserveCallWindow({ ...panelPosition, ...IN_APP_PANEL_SIZE })) {
      toast.error(
        t('chat:call.errors.popup_blocked', {
          defaultValue:
            'Allow pop-ups for this site to start a call — the call needs its own window to use your microphone.',
        })
      )
      return
    }

    try {
      const workspaceId = await getWorkspaceContext()
      if (!workspaceId) {
        closeCallWindow()
        toast.error(t('chat:interface.errors.select_workspace'))
        return
      }

      const meetingToken = await startInAppMeeting({
        agentId: agent.id,
        workspaceId,
        translatorMode: false,
        agentName: agent.name,
        entireDataCatalog,
      })

      if (embedded && !launchCall(meetingToken)) {
        // The reservation was closed mid-flight and the fallback `window.open`
        // ran after the awaits, with expired activation, so it was blocked. The
        // session is live and recoverable — the panel's Reopen button runs from a
        // fresh click — so keep it and say so rather than claiming the call is
        // running in a window that does not exist.
        toast.error(
          t('chat:call.errors.popup_blocked', {
            defaultValue:
              'Allow pop-ups for this site to start a call — the call needs its own window to use your microphone.',
          })
        )
      }

      trackEvent('ai_call-started', {
        agent_id: agent.id,
        agent_name: agent.name,
        meeting_platform: embedded ? 'in_app_window' : 'in_app',
        translator_mode_enabled: false,
        entire_data_catalog_enabled: entireDataCatalog,
      })
    } catch (error) {
      closeCallWindow()
      console.error('Error starting in-app call:', error)
      const errorMessage = error instanceof Error ? error.message : ''
      trackEvent('ai_call-failed', {
        agent_id: agent.id,
        error_type: errorMessage || 'unknown',
      })
      toast.error(t('chat:call_config.errors.failed_to_start'))
    }
  }

  // Same agent roster in both menus below; only what picking one does differs.
  const agentItems = (onPick: (agent: Agent) => void) =>
    onlineAgents.length > 0 ? (
      onlineAgents.map((agent) => (
        <DropdownMenuItem
          key={agent.id}
          onSelect={() => onPick(agent)}
          className={clsx(
            'flex items-center gap-2',
            isRTL ? 'flex-row-reverse' : 'flex-row'
          )}
        >
          <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
            <img
              src={agent.image}
              alt={agent.name}
              className="w-full h-full object-cover"
            />
          </div>
          <span className="text-sm font-medium">{agent.title}</span>
        </DropdownMenuItem>
      ))
    ) : (
      <DropdownMenuItem disabled>
        <div
          className={clsx(
            'flex items-center gap-2',
            isRTL ? 'text-right justify-end' : 'text-left'
          )}
        >
          <div className="text-sm text-gray-500 dark:text-dark-text-muted">
            {t('chat:call.no_agents_message')}
          </div>
        </div>
      </DropdownMenuItem>
    )

  return (
    <>
      {/* Voice: the in-app call. Was "Call now" inside each agent's submenu — it has
          its own button now, so picking an agent starts the call directly. */}
      <DropdownMenu>
        <TooltipOverlay message={t('chat:call.call_now')}>
          <DropdownMenuTrigger asChild className="focus-visible:border-none">
            {compact ? (
              <button
                type="button"
                data-testid="in-app-call-button"
                className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
              >
                <Phone className="w-[18px] h-[18px]" />
              </button>
            ) : (
              <Button
                data-testid="in-app-call-button"
                variant="outline"
                className="rounded-full w-[40px] h-[40px] p-4 hover:border-orange-500 hover:bg-orange-500 hover:text-white transition-colors"
              >
                <Phone className="w-8 h-8" />
              </Button>
            )}
          </DropdownMenuTrigger>
        </TooltipOverlay>
        {/* The press on an agent is where the call window learns the frame's on-screen
            position, so it can dock exactly beside the call panel. */}
        <DropdownMenuContent align={isRTL ? 'start' : 'end'} onPointerDown={rememberFrameOrigin}>
          <DropdownMenuLabel
            className={clsx(isRTL ? 'text-right' : 'text-left')}
          >
            {t('chat:call.call_now')}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {/* Scope for the call started from here. `preventDefault` keeps the menu open so the
              user can tick it and then pick an agent — ticking it must not start a call. Same
              label and default as the chat footer toggle and the call-config modal.
              Gated like the chat toggle: an embed host decides which documents a board may
              see, and catalog scope would let the call read past that grant. */}
          {isFeatureAllowedInEmbed('catalogScope') && (
            <>
              <DropdownMenuCheckboxItem
                data-testid="entire-data-catalog-call-checkbox"
                checked={entireDataCatalog}
                onCheckedChange={setEntireDataCatalog}
                onSelect={(e) => e.preventDefault()}
                className={clsx(isRTL ? 'text-right' : 'text-left')}
              >
                {t('chat:call_config.entire_data_catalog')}
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
            </>
          )}
          {agentItems((agent) => {
            handleStartInApp(agent).catch((error) => {
              console.error('Failed to start in-app call', error)
            })
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Video: joining an EXTERNAL meeting (Meet/Teams/Zoom via Recall), which is what
          `meetingIntegration` gates — and now all this button does, so the gate covers
          the whole thing rather than one item inside it. */}
      {isFeatureAllowedInEmbed('meetingIntegration') && (
        <DropdownMenu>
          <TooltipOverlay message={t('chat:call.dropdown_message')}>
            <DropdownMenuTrigger asChild className="focus-visible:border-none">
              {compact ? (
                <button
                  type="button"
                  data-testid="record-button"
                  className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
                >
                  <Video className="w-[18px] h-[18px]" />
                </button>
              ) : (
                <Button
                  data-testid="record-button"
                  variant="outline"
                  className="rounded-full w-[40px] h-[40px] p-4 hover:border-orange-500 hover:bg-orange-500 hover:text-white transition-colors"
                >
                  <Video className="w-8 h-8" />
                </Button>
              )}
            </DropdownMenuTrigger>
          </TooltipOverlay>
          <DropdownMenuContent align={isRTL ? 'start' : 'end'}>
            <DropdownMenuLabel
              className={clsx(isRTL ? 'text-right' : 'text-left')}
            >
              {t('chat:call.add_existing_meeting')}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {agentItems(async (agent) => {
              const workspaceId = await getWorkspaceContext()
              if (!workspaceId) {
                toast.error(t('chat:interface.errors.select_workspace'))
                return
              }
              setSelectedAgentForCall(agent)
              setTimeout(() => {
                openModal(MODAL_TYPE.callConfig)
              }, 0)
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  )
}

export default LLMCallDropdown
